import { useCallback, useEffect, useRef, useState } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import type { EbookRuntimeConfig } from "@ebook/contracts";
import { useSearch } from "@tanstack/react-router";
import { useTranslate } from "../i18n";
import { api } from "../lib/api-client";
import { readerDevPreviewUrl } from "../lib/reader-dev-url";

function isReaderReadyMessage(data: unknown): data is { type: "ebook-reader-ready" } {
  return typeof data === "object"
    && data !== null
    && "type" in data
    && (data as { type?: unknown }).type === "ebook-reader-ready";
}

export function EbookPreviewPage() {
  const { ebookId, token } = useSearch({ from: "/preview" });
  const [preview, setPreview] = useState<Pick<EbookRuntimeConfig, "title" | "backgroundColor"> | null>(null);
  const [loading, setLoading] = useState(true);
  const [frameLoading, setFrameLoading] = useState(true);
  const [error, setError] = useState("");
  const frameFallbackTimer = useRef<number | null>(null);
  const __ = useTranslate();

  const localHosts = new Set(["localhost", "127.0.0.1", "::1"]);
  const useReaderDevServer = import.meta.env.DEV && localHosts.has(window.location.hostname);
  const frameUrl = ebookId
    ? token
      ? `/api/ebooks/${encodeURIComponent(ebookId)}/preview/?token=${encodeURIComponent(token)}`
      : `/api/ebooks/${encodeURIComponent(ebookId)}/preview/`
    : "";
  const readerFrameUrl = useReaderDevServer && ebookId
    ? readerDevPreviewUrl(
        import.meta.env.VITE_READER_DEV_ORIGIN,
        window.location,
        ebookId,
      )
    : frameUrl;
  const readerFrameOrigin = readerFrameUrl
    ? new URL(readerFrameUrl, window.location.origin).origin
    : window.location.origin;

  const clearFrameFallback = useCallback(() => {
    if (frameFallbackTimer.current === null) return;
    window.clearTimeout(frameFallbackTimer.current);
    frameFallbackTimer.current = null;
  }, []);

  const markFrameReady = useCallback(() => {
    clearFrameFallback();
    setFrameLoading(false);
  }, [clearFrameFallback]);

  const scheduleFrameFallback = useCallback(() => {
    clearFrameFallback();
    frameFallbackTimer.current = window.setTimeout(() => {
      setFrameLoading(false);
      frameFallbackTimer.current = null;
    }, 5_000);
  }, [clearFrameFallback]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setPreview(null);
    if (!ebookId) {
      setError(__("ไม่พบ Ebook ID ใน Preview URL"));
      setLoading(false);
      return;
    }
    try {
      if (token) {
        const config = await api.previewConfig(ebookId, token);
        setPreview({
          title: config.title,
          backgroundColor: config.backgroundColor,
        });
        return;
      }
      const next = await api.ebook(ebookId);
      if (next.status !== "ready") {
        setError(__("E-book ต้องประมวลผลเสร็จแล้วจึงจะ Preview ได้"));
        return;
      }
      setPreview({
        title: next.title,
        backgroundColor: next.backgroundColor,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("เปิด Preview ไม่สำเร็จ"));
    } finally {
      setLoading(false);
    }
  }, [__, ebookId, token]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (preview && ebookId) {
      clearFrameFallback();
      setFrameLoading(true);
    }
  }, [clearFrameFallback, ebookId, preview]);
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== readerFrameOrigin || !isReaderReadyMessage(event.data)) return;
      markFrameReady();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [markFrameReady, readerFrameOrigin]);
  useEffect(() => () => clearFrameFallback(), [clearFrameFallback]);
  useEffect(() => {
    if (!preview) return;
    const previousTitle = document.title;
    document.title = `${preview.title} — Preview`;
    return () => { document.title = previousTitle; };
  }, [preview]);

  if (loading) {
    return <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <CircularProgress aria-label={__("กำลังโหลด Preview")} />
    </Box>;
  }

  if (error || !preview || !ebookId) {
    return <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3 }}>
      <Box sx={{ width: "min(100%, 520px)" }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || __("เปิด Preview ไม่สำเร็จ")}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => window.close()}>
          {__("ปิด Preview")}
        </Button>
        <Button onClick={() => void load()}>{__("ลองอีกครั้ง")}</Button>
      </Box>
    </Box>;
  }

  return <Box sx={{ position: "fixed", inset: 0, bgcolor: preview.backgroundColor }}>
    {frameLoading && <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        display: "grid",
        placeItems: "center",
        bgcolor: preview.backgroundColor,
      }}
    >
      <Box textAlign="center">
        <CircularProgress aria-label={__("กำลังเปิด E-book")} />
        <Typography mt={2}>{__("กำลังเปิด {{title}}", { title: preview.title })}</Typography>
      </Box>
    </Box>}
    <Box
      component="iframe"
      title={__("Preview {{title}}", { title: preview.title })}
      src={readerFrameUrl}
      onLoad={scheduleFrameFallback}
      sx={{ display: "block", width: "100%", height: "100%", border: 0 }}
      allow="fullscreen"
    />
  </Box>;
}
